import { SingleExecutionResult } from '@apollo/client';
import { AnalyticIdentifier, assertNever, createError } from '@common/neo.tax';
import { AlertModalLayout, Button } from '@ui-components/neo.tax';
import { useState } from 'react';

import {
  DeleteAccountingSyncEventForFiscalYearMutation,
  DeletePayrollSyncEventForFiscalYearMutation,
} from '@neo.tax/shared/graphql';
import {
  useDeleteAccountingSyncEventForFiscalYear,
  useDeletePayrollSyncEventForFiscalYear,
} from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';
import { DataSourcesTableItem } from '@neo.tax/shared/utils';

import { GeneralFormErrorStyled, ModalContainer } from './delete-fiscal-year-provider.styled';

interface DeleteFiscalYearProviderProps {
  fiscalYearId: string;
  accountTableItem: DataSourcesTableItem | undefined;
  close: () => void;
}

export const DeleteFiscalYearProvider = ({
  accountTableItem,
  close,
  fiscalYearId,
}: DeleteFiscalYearProviderProps): React.ReactElement | null => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const confirmBtnId = useAnalyticId(AnalyticIdentifier.DELETE_CONNECTED_DATA_SOURCE_CONFIRM_BUTTON);
  const cancelBtnId = useAnalyticId(AnalyticIdentifier.DELETE_CONNECTED_DATA_SOURCE_CANCEL_BUTTON);

  const [deleteAccountingSyncEventForFiscalYear, { loading: loadingAccountingProviderDelete }] =
    useDeleteAccountingSyncEventForFiscalYear(fiscalYearId);
  const [deletePayrollSyncEventForFiscalYear, { loading: loadingPayrollProviderDelete }] =
    useDeletePayrollSyncEventForFiscalYear(fiscalYearId);
  const loading = loadingAccountingProviderDelete || loadingPayrollProviderDelete;
  const isFileUpload = accountTableItem?.itemType === 'rd-payroll-cost';
  const actionLabel = isFileUpload ? 'Delete' : 'Disconnect';
  const actionLabelLowercase = isFileUpload ? 'delete' : 'disconnect';
  const loadingLabel = isFileUpload ? 'Deleting...' : 'Disconnecting...';
  const dataSourceName = accountTableItem?.title ?? 'Data Source';

  const deleteProvider = (
    fiscalYearId: string,
    accountTableItem: DataSourcesTableItem,
  ): Promise<
    | SingleExecutionResult<DeleteAccountingSyncEventForFiscalYearMutation, Record<string, any>, Record<string, any>>
    | SingleExecutionResult<DeletePayrollSyncEventForFiscalYearMutation, Record<string, any>, Record<string, any>>
  > => {
    switch (accountTableItem.itemType) {
      case 'accounting':
        return deleteAccountingSyncEventForFiscalYear({
          variables: {
            fiscalYearId,
            accountingProviderSyncEventId: accountTableItem.id,
          },
        });
      case 'payroll':
      case 'rd-payroll-cost':
        return deletePayrollSyncEventForFiscalYear({
          variables: {
            fiscalYearId,
            payrollProviderSyncEventId: accountTableItem.id,
          },
        });
      case 'ticketing-file':
      case 'accounting-token':
      case 'payroll-token':
      case 'tax-document':
      case 'timesheet':
      case 'asc-cost':
      case 'payroll-month-expense':
      case 'github-email-mapping':
        throw createError(
          'DeleteProviderError',
          `Cannot delete ${accountTableItem.itemType} provider. Provider does not exist within the context of a fiscal year.`,
        );
      default:
        return assertNever(accountTableItem.itemType);
    }
  };

  const onSubmit = async (): Promise<void> => {
    if (!fiscalYearId || !accountTableItem) {
      return;
    }

    try {
      await deleteProvider(fiscalYearId, accountTableItem);
      close();
    } catch {
      setErrorMessage('Something unexpected prevented the account from being deleted. Please try again.');
    }
  };

  const onClickCancel = (): void => {
    close();
  };

  return (
    <ModalContainer>
      <AlertModalLayout
        title={`${actionLabel} ${dataSourceName}`}
        copy={`This will permanently remove all expenses from this data source and all allocations associated with those expenses. Are you sure you want to ${actionLabelLowercase} ${
          accountTableItem ? `${accountTableItem.title}` : 'this data source'
        }?`}
        trailingContent={
          <>
            <Button id={cancelBtnId} size="base" variant="white" rounded="regular" onClick={onClickCancel}>
              Cancel
            </Button>
            <Button
              id={confirmBtnId}
              disabled={loading}
              rounded="regular"
              size="base"
              type="submit"
              variant="rsbry"
              onClick={onSubmit}
            >
              {loading ? loadingLabel : 'Confirm'}
            </Button>
          </>
        }
      >
        <GeneralFormErrorStyled centered error={!!errorMessage}>
          {errorMessage}
        </GeneralFormErrorStyled>
      </AlertModalLayout>
    </ModalContainer>
  );
};
